import React, { useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/authcontext';
import { useEmailProviders } from '../hooks/useEmailProviders';
import { all_routes } from '../feature-module/router/all_routes';
import NoAccountConnected from './NoAccountConnected';

interface EmailProviderGuardProps {
  children: React.ReactNode;
  fallbackRoute?: string;
  showNoAccountMessage?: boolean;
}

const EmailProviderGuard: React.FC<EmailProviderGuardProps> = ({ 
  children, 
  fallbackRoute = all_routes.email,
  showNoAccountMessage = true 
}) => {
  const { auth } = useContext(AuthContext);
  const { hasConnectedProviders, loading } = useEmailProviders();
  const location = useLocation();

  // If not authenticated, redirect to login
  if (!auth) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // While checking email providers, show loading
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // If no email providers are connected, handle based on configuration
  if (!hasConnectedProviders) {
    if (showNoAccountMessage) {
      // Show the no account connected message instead of redirecting
      return <NoAccountConnected />;
    } else {
      // Redirect to fallback route
      return <Navigate to={fallbackRoute} replace />;
    }
  }

  // If user has connected providers, allow access
  return <>{children}</>;
};

export default EmailProviderGuard;